/**
 * Admin · Email Failures By Category Tile (Phase 2L-A136, v2 Feb 2026)
 *
 * Read-only operator visibility for outbound email failures, grouped
 * by operator-friendly category bucket. Sits next to the existing
 * Communications Health tile on the Admin Analytics Dashboard.
 *
 * v2 adds:
 *   • per-bucket "View failures" drill-down modal
 *   • CSV export of the current view (bucket + window)
 *
 * NEVER renders for non-admins (route is admin-gated upstream).
 * NEVER mutates state — pure GET on read-only endpoints.
 * NEVER surfaces payload HTML, persisted reply_to, or any token.
 *
 * Calm green/neutral state when no failures detected in the last 30d.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MailWarning, Loader2, RefreshCw, AlertTriangle, CheckCircle2,
  ArrowRight, ShieldAlert, Clock, Download, X, Search,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { fetchAndOpenAuthed } from "@/lib/authedDownload";

const WINDOW_COL = [
  { id: "count_24h", label: "24h" },
  { id: "count_7d",  label: "7d"  },
  { id: "count_30d", label: "30d" },
];

function tone(count) {
  if (count >= 5)  return { bg: "bg-rose-50",   text: "text-rose-700",   border: "border-rose-200" };
  if (count > 0)   return { bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200" };
  return { bg: "bg-slate-50", text: "text-slate-400", border: "border-slate-200" };
}

export function EmailFailuresByCategoryTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState(null);     // { bucket, label, window }
  const [csvBusy, setCsvBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/email-health/failures-by-category");
      setData(res.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDrill = (bucket, label, window) => {
    setDrill({ bucket, label, window });
  };
  const closeDrill = () => setDrill(null);

  const exportCsv = useCallback(async (bucket, window = "30d") => {
    setCsvBusy(true);
    try {
      const qs = new URLSearchParams();
      if (bucket) qs.set("bucket", bucket);
      qs.set("window", window);
      await fetchAndOpenAuthed({
        path: `/admin/email-health/failures-by-category/export.csv?${qs.toString()}`,
        mode: "download",
        loadingMessage: "Preparing CSV…",
      });
    } finally {
      setCsvBusy(false);
    }
  }, []);

  const items = data?.items || [];
  const totals = data?.totals || { count_24h: 0, count_7d: 0, count_30d: 0 };
  const webhook = data?.webhook_failures || { count_24h: 0, count_7d: 0, count_30d: 0 };
  const stale = data?.stale_send_failures || 0;
  const totalAll30d = totals.count_30d;
  const allClean = !loading && !error && totalAll30d === 0
    && webhook.count_30d === 0 && stale === 0;

  return (
    <div
      data-testid="admin-email-failures-by-category-tile"
      className="rounded-3xl bg-white border border-slate-200 p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <MailWarning className="w-4 h-4 text-amber-600" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
            Email failures · by category
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid="admin-email-failures-export-all"
            title="Export all failures (30d) as CSV"
            onClick={() => exportCsv(null, "30d")}
            disabled={csvBusy || loading || totalAll30d === 0}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {csvBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Export CSV
          </button>
          <button
            type="button"
            data-testid="admin-email-failures-refresh"
            title="Refresh"
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {error ? (
        <div
          data-testid="admin-email-failures-error"
          className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      ) : loading && !data ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : allClean ? (
        <div
          data-testid="admin-email-failures-empty"
          className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-semibold text-slate-900 text-sm">No recent outbound email failures detected.</div>
            <div className="text-xs text-slate-500 mt-0.5">Scanned the last 30 days of outbound email logs, Resend webhook events, and stale failures.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Top headline counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="admin-email-failures-headline">
            <HeadlineTile testid="headline-24h" label="Failures · 24h" value={totals.count_24h} tone={tone(totals.count_24h)} />
            <HeadlineTile testid="headline-7d"  label="Failures · 7d"  value={totals.count_7d}  tone={tone(totals.count_7d)} />
            <HeadlineTile testid="headline-30d" label="Failures · 30d" value={totals.count_30d} tone={tone(totals.count_30d)} />
            <HeadlineTile testid="headline-stale" label="Stale > 7d"   value={stale}            tone={tone(stale)} icon={Clock} />
          </div>

          {/* Webhook failures */}
          {(webhook.count_30d > 0) && (
            <div
              data-testid="admin-email-failures-webhook-banner"
              className="mb-4 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50/60 p-3"
            >
              <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0" />
              <div className="text-xs text-slate-700">
                <span className="font-semibold text-rose-700">{webhook.count_30d}</span> Resend webhook signature/verification failure
                {webhook.count_30d === 1 ? "" : "s"} in the last 30d
                {" "}<span className="text-slate-500">·</span>{" "}
                {webhook.count_24h} in the last 24h
                {" "}<span className="text-slate-500">·</span>{" "}
                {webhook.count_7d} in the last 7d.
              </div>
            </div>
          )}

          {/* Per-category table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm" data-testid="admin-email-failures-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Category</th>
                  {WINDOW_COL.map((w) => (
                    <th key={w.id} className="text-right px-3 py-2" data-testid={`admin-email-failures-th-${w.id}`}>{w.label}</th>
                  ))}
                  <th className="text-left px-3 py-2">Latest failure</th>
                  <th className="text-left px-3 py-2">Latest error</th>
                  <th className="text-right px-3 py-2">Logs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.filter((r) => r.count_30d > 0).map((row) => (
                  <tr
                    key={row.bucket}
                    data-testid={`admin-email-failures-row-${row.bucket}`}
                    className="bg-white"
                  >
                    <td className="px-3 py-2.5 text-slate-800 font-medium">{row.label}</td>
                    {WINDOW_COL.map((w) => {
                      const v = row[w.id];
                      const t = tone(v);
                      const win = w.id.replace("count_", "");
                      const canDrill = v > 0;
                      return (
                        <td key={w.id} className="px-3 py-2.5 text-right">
                          {canDrill ? (
                            <button
                              type="button"
                              data-testid={`admin-email-failures-${row.bucket}-${w.id}`}
                              onClick={() => openDrill(row.bucket, row.label, win)}
                              className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-[11px] font-bold border ${t.bg} ${t.text} ${t.border} tabular-nums hover:brightness-95 hover:shadow-sm transition cursor-pointer`}
                              title={`View ${v} failure${v === 1 ? "" : "s"} · ${w.label}`}
                            >
                              {v}
                            </button>
                          ) : (
                            <span
                              data-testid={`admin-email-failures-${row.bucket}-${w.id}`}
                              className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-[11px] font-bold border ${t.bg} ${t.text} ${t.border} tabular-nums`}
                            >
                              {v}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5 text-slate-600 text-[11px] whitespace-nowrap">
                      {row.latest_at ? formatDateAU(row.latest_at) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500 text-[11px] max-w-[280px] truncate" title={row.latest_error || ""}>
                      {row.latest_error || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openDrill(row.bucket, row.label, "30d")}
                          data-testid={`admin-email-failures-${row.bucket}-drilldown`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900"
                        >
                          <Search className="w-3 h-3" /> View failures
                        </button>
                        <Link
                          to="/admin/emails?status_filter=failed"
                          data-testid={`admin-email-failures-${row.bucket}-link`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
                        >
                          Full logs <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.filter((r) => r.count_30d > 0).length === 0 && totalAll30d === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-xs text-slate-500" data-testid="admin-email-failures-categories-empty">
                      No category-level failures recorded — see headline counters above for webhook/stale signals.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer note */}
          <div className="mt-3 text-[11px] text-slate-500">
            Read-only view. Source: <code className="text-[10px] bg-slate-50 px-1 rounded">email_logs</code> · status ∈ {"{"} failed, skipped_no_key {"}"}. Errors are truncated and never include payload HTML or tokens.
          </div>
        </>
      )}

      {drill && (
        <FailuresDrilldownModal
          bucket={drill.bucket}
          bucketLabel={drill.label}
          window={drill.window}
          onClose={closeDrill}
          onExport={() => exportCsv(drill.bucket, drill.window)}
        />
      )}
    </div>
  );
}

function FailuresDrilldownModal({ bucket, bucketLabel, window, onClose, onExport }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [truncated, setTruncated] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const qs = new URLSearchParams({ bucket, window, limit: "200" });
        const res = await api.get(`/admin/email-health/failures-by-category/drilldown?${qs.toString()}`);
        if (!alive) return;
        setRows(res.data?.rows || []);
        setCount(res.data?.count || 0);
        setTruncated(!!res.data?.truncated);
      } catch (e) {
        if (!alive) return;
        setErr(formatApiError(e?.response?.data?.detail || e.message));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [bucket, window]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="admin-email-failures-drilldown"
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-slate-900/50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-4xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 flex items-center gap-2">
              <MailWarning className="w-3.5 h-3.5" />
              Failures drill-down
            </div>
            <h3 className="text-lg font-display font-bold text-slate-900 mt-1" data-testid="admin-email-failures-drilldown-title">
              {bucketLabel} · last {window}
            </h3>
            <div className="text-xs text-slate-500 mt-0.5">
              {loading ? "Loading…" : `${count} row${count === 1 ? "" : "s"}${truncated ? " (truncated — export CSV for the full list)" : ""}`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onExport}
              data-testid="admin-email-failures-drilldown-export"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="admin-email-failures-drilldown-close"
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {err ? (
            <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <div>{err}</div>
            </div>
          ) : loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading failures…
            </div>
          ) : rows.length === 0 ? (
            <div
              data-testid="admin-email-failures-drilldown-empty"
              className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4"
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-slate-700">No failures recorded in this window.</div>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs" data-testid="admin-email-failures-drilldown-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Category</th>
                    <th className="text-left px-3 py-2">To</th>
                    <th className="text-left px-3 py-2">Subject</th>
                    <th className="text-left px-3 py-2">Error</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="align-top" data-testid={`admin-email-failures-drilldown-row-${r.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600 font-mono text-[11px]">
                        {r.created_at ? formatDateAU(r.created_at) : "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-700 font-mono text-[11px]">{r.category || "—"}</td>
                      <td className="px-3 py-2 text-slate-700 truncate max-w-[180px]" title={r.to || ""}>
                        {r.to || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-600 truncate max-w-[220px]" title={r.subject || ""}>
                        {r.subject || "—"}
                      </td>
                      <td className="px-3 py-2 text-rose-700 max-w-[260px]">
                        <span className="line-clamp-2 break-words" title={r.error || ""}>
                          {r.error || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border border-rose-200 bg-rose-50 text-rose-700">
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl text-[11px] text-slate-500">
          Read-only. Payload HTML, reply-to and tokens are never returned. Newest failures first, capped at 200 rows — use CSV export for the full range.
        </div>
      </div>
    </div>
  );
}

function HeadlineTile({ testid, label, value, tone: t, icon: Icon }) {
  return (
    <div
      data-testid={`admin-email-failures-${testid}`}
      className={`rounded-2xl border px-3 py-3 ${t.border} ${t.bg}`}
    >
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold ${t.text}`}>
        {Icon ? <Icon className="w-3 h-3" /> : <MailWarning className="w-3 h-3" />}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-2xl font-display font-bold leading-tight mt-1 text-slate-900 tabular-nums">
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}
